/**
 * PATH: apps/quote/src/pages/pricelist-page/SuppliersTab.tsx
 * PURPOSE: Supplier quotes listing tab
 */

import { FileSpreadsheet, ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils';

interface SuppliersTabProps {
  supplierQuotes: any[];
  supplierQuotesLoading: boolean;
}

export default function SuppliersTab({ supplierQuotes, supplierQuotesLoading }: SuppliersTabProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-gray-500">Upload supplier pricelists to extract pricing using AI</p>
        <a
          href="/supplier-quotes"
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <FileSpreadsheet className="h-4 w-4" />
          Manage Supplier Quotes
          <ChevronRight className="h-4 w-4" />
        </a>
      </div>

      {supplierQuotesLoading ? (
        <div className="p-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : supplierQuotes.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8 text-center">
          <FileSpreadsheet className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No supplier quotes uploaded</h3>
          <p className="text-gray-500 mb-4">Upload PDF or Excel files from suppliers to extract prices with AI.</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Supplier</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Reference</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Items</th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Status</th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {supplierQuotes.slice(0, 10).map((quote) => (
                <tr key={quote.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">{quote.supplier_name || 'Unknown'}</td>
                  <td className="px-6 py-4 text-gray-600 dark:text-gray-400">{quote.quote_reference || '—'}</td>
                  <td className="px-6 py-4 text-right text-gray-600 dark:text-gray-400">{quote.line_count || 0}</td>
                  <td className="px-6 py-4">
                    <span className={cn(
                      "px-2 py-1 rounded-full text-xs font-medium",
                      quote.status === 'extracted' && "bg-green-100 text-green-800",
                      quote.status === 'pending' && "bg-yellow-100 text-yellow-800",
                      quote.status === 'processing' && "bg-blue-100 text-blue-800",
                      quote.status === 'failed' && "bg-red-100 text-red-800"
                    )}>
                      {quote.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right font-medium text-gray-900 dark:text-white">
                    {quote.total ? formatCurrency(parseFloat(String(quote.total))) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
